import type {
  ContradictionFieldRef,
  ContradictionFinding,
} from '../../../services/api/types.gen';
import { ACTIVE_DIAGNOSIS_CATEGORY } from '../OASISFormLabels';
import {
  escapeCssAttributeValue,
  isVisibleOasisNavigationTarget,
} from '../oasisNavigationVisibility';

/** Non-qid contradiction anchors (active diagnoses / diagnosis list). */
export function findContradictionSourceTarget(
  location: ContradictionFieldRef
): HTMLElement | null {
  const source = escapeCssAttributeValue(location.source);
  const field = location.field ? escapeCssAttributeValue(location.field) : null;

  const selectors = field
    ? [
        `[data-contradiction-source="${source}"][data-contradiction-field="${field}"]`,
        `[data-contradiction-source="${source}"]`,
      ]
    : [`[data-contradiction-source="${source}"]`];

  for (const selector of selectors) {
    const nodes = document.querySelectorAll<HTMLElement>(selector);
    for (const node of nodes) {
      if (isVisibleOasisNavigationTarget(node)) return node;
    }
  }
  return null;
}

export function contradictionLocationNeedsActiveDiagnosisCategory(
  location: ContradictionFieldRef
): boolean {
  return (
    location.source === 'active_diagnoses' ||
    location.source === 'diagnosis_list'
  );
}

/** Sections a single error_location belongs to (for scrubber filter + jump). */
export function categoriesForContradictionLocation(
  location: ContradictionFieldRef,
  qidToCategoryMap: Record<string, string>
): string[] {
  if (contradictionLocationNeedsActiveDiagnosisCategory(location)) {
    return [ACTIVE_DIAGNOSIS_CATEGORY];
  }
  return Array.from(
    new Set(
      (location.qids || [])
        .map(qid => qidToCategoryMap[qid])
        .filter((category): category is string => Boolean(category))
    )
  );
}

export function contradictionLocationIsOnCategory(
  location: ContradictionFieldRef,
  category: string | null,
  qidToCategoryMap: Record<string, string>
): boolean {
  if (!category) return false;
  return categoriesForContradictionLocation(
    location,
    qidToCategoryMap
  ).includes(category);
}

/** Primary section id for a location (first related category), if any. */
export function primaryCategoryForContradictionLocation(
  location: ContradictionFieldRef,
  qidToCategoryMap: Record<string, string>
): string | null {
  return (
    categoriesForContradictionLocation(location, qidToCategoryMap)[0] ?? null
  );
}

/**
 * Card-body jump target when nothing is on the open section: first
 * ``error_locations`` entry (rule JSON order). Callers that already found
 * on-page locations highlight those directly and do not use this helper.
 */
export function pickContradictionJumpLocation(
  finding: ContradictionFinding,
  currentCategory: string | null,
  qidToCategoryMap: Record<string, string>
): ContradictionFieldRef | null {
  const locations = finding.error_locations || [];
  if (locations.length === 0) return null;

  const local = locations.find(location =>
    contradictionLocationIsOnCategory(
      location,
      currentCategory,
      qidToCategoryMap
    )
  );
  if (local) return local;

  return locations[0];
}

/** Codes to resolve for suggestion copy on a finding's remediation action. */
export function suggestionCodesFromAction(
  action: { [key: string]: unknown } | null | undefined
): string[] {
  if (!action || typeof action !== 'object') return [];
  const code = action.code;
  if (typeof code === 'string' && code.trim()) return [code.trim()];
  const candidates = action.candidate_codes;
  if (Array.isArray(candidates)) {
    return candidates.filter(
      (c): c is string => typeof c === 'string' && c.trim().length > 0
    );
  }
  return [];
}
